import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../client.ts';
import type { ViewersResponse } from '../bindings/ViewersResponse.ts';
import type { InviteOutcome } from '../bindings/InviteOutcome.ts';
import type { ShareLinksResponse } from '../bindings/ShareLinksResponse.ts';
import type { CreateShareLinkResponse } from '../bindings/CreateShareLinkResponse.ts';
import type { PlanStatsResponse } from '../bindings/PlanStatsResponse.ts';
import type { SharedPlanResponse } from '../bindings/SharedPlanResponse.ts';
import type { MeResponse } from '../bindings/MeResponse.ts';
import type { AcceptInviteRequest } from '../bindings/AcceptInviteRequest.ts';

export function useViewers(planId: string) {
  return useQuery({
    queryKey: ['viewers', planId],
    queryFn: () => apiFetch<ViewersResponse>(`/v1/plans/${planId}/viewers`),
  });
}

export function useInviteViewer(planId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (email: string) =>
      apiFetch<InviteOutcome>(`/v1/plans/${planId}/viewers`, {
        method: 'POST',
        body: JSON.stringify({ email }),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['viewers', planId] }),
  });
}

export function useRevokeViewer(planId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (viewerId: string) =>
      apiFetch<void>(`/v1/plans/${planId}/viewers/${viewerId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['viewers', planId] });
      void queryClient.invalidateQueries({ queryKey: ['stats', planId] });
    },
  });
}

export function useRevokeInvite(planId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) =>
      apiFetch<void>(`/v1/plans/${planId}/invites/${inviteId}`, { method: 'DELETE' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['viewers', planId] }),
  });
}

export function useShareLinks(planId: string) {
  return useQuery({
    queryKey: ['share-links', planId],
    queryFn: () => apiFetch<ShareLinksResponse>(`/v1/plans/${planId}/share-links`),
  });
}

export function useCreateShareLink(planId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (label: string) =>
      apiFetch<CreateShareLinkResponse>(`/v1/plans/${planId}/share-links`, {
        method: 'POST',
        body: JSON.stringify({ label }),
      }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['share-links', planId] }),
  });
}

export function useRevokeShareLink(planId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (linkId: string) =>
      apiFetch<void>(`/v1/plans/${planId}/share-links/${linkId}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['share-links', planId] });
      void queryClient.invalidateQueries({ queryKey: ['stats', planId] });
    },
  });
}

export function usePlanStats(planId: string) {
  return useQuery({
    queryKey: ['stats', planId],
    queryFn: () => apiFetch<PlanStatsResponse>(`/v1/plans/${planId}/stats`),
  });
}

export function useSharedPlan(token: string | undefined) {
  return useQuery({
    queryKey: ['shared', token],
    queryFn: () => apiFetch<SharedPlanResponse>(`/v1/shared/${token}`),
    enabled: !!token,
    retry: false,
  });
}

export function recordSharedEvent(token: string, sectionSlug?: string) {
  void apiFetch<void>(`/v1/shared/${token}/events`, {
    method: 'POST',
    body: JSON.stringify({ section_slug: sectionSlug }),
  }).catch(() => undefined);
}

export function recordPlanEvent(planId: string, sectionSlug?: string) {
  void apiFetch<void>(`/v1/plans/${planId}/events`, {
    method: 'POST',
    body: JSON.stringify({ section_slug: sectionSlug }),
  }).catch(() => undefined);
}

export function useAcceptInvite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (req: AcceptInviteRequest) =>
      apiFetch<MeResponse>('/v1/invites/accept', { method: 'POST', body: JSON.stringify(req) }),
    onSuccess: (me) => queryClient.setQueryData(['me'], me),
  });
}
